import {
  Building2,
  Button,
  CreditCard,
  DollarSign,
  FileText,
  Rocket,
  ToggleRight,
  UserPlus,
} from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { StatisticsCard } from "../components/molecules/StatisticsCard";
import { DashboardInsights } from "./components/DashboardInsights";
import { NewSignUps } from "./components/NewSignUps";
import { useScreenLocked } from "../components/common/pinLockContext";
import { adminStatsApi } from "../services/adminStats.api";
import { AdminOverviewStats } from "../types/adminStats.definition";
import { formatMoney } from "../utils/money-utils";
import { percentChange } from "../utils/stats-utils";

const NO_VALUE = "—";

export const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminOverviewStats | null>(null);
  const [loadError, setLoadError] = useState(false);

  // MED-1333: the cards show the platform totals from the admin overview stats, never placeholder numbers.
  const loadStats = useCallback((signal: { cancelled: boolean }) => {
    adminStatsApi
      .getOverview()
      .then(data => {
        if (!signal.cancelled) {
          setStats(data);
          setLoadError(false);
        }
      })
      .catch(() => {
        if (!signal.cancelled) {
          setLoadError(true);
        }
      });
  }, []);

  // MED-1622: while a lock screen covers the page every call answers 423, so the
  // refreshes below are skipped and the stats reload once the screen is unlocked.
  const locked = useScreenLocked();
  const lockedRef = useRef(locked);
  lockedRef.current = locked;
  const wasLocked = useRef(locked);
  useEffect(() => {
    if (wasLocked.current && !locked) {
      const signal = { cancelled: false };
      loadStats(signal);
      wasLocked.current = locked;
      return () => {
        signal.cancelled = true;
      };
    }
    wasLocked.current = locked;
  }, [locked, loadStats]);

  // MED-1452: the overview used to fetch once and never again, so in a long session its
  // facility count drifted away from the one the Établissements page was showing. It is
  // refreshed whenever the admin comes back to the tab.
  useEffect(() => {
    const signal = { cancelled: false };
    loadStats(signal);

    const refresh = () => {
      if (document.visibilityState === "visible" && !lockedRef.current) {
        loadStats(signal);
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      signal.cancelled = true;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadStats]);

  const revenueTrend = stats
    ? percentChange(stats.revenueCurrentMonth, stats.revenuePreviousMonth)
    : null;

  return (
    <div className="page-container">
      {/* Onboard New Facility CTA */}
      <div className="overflow-hidden bg-gradient-to-r rounded-lg shadow-lg page-section from-primary-600 to-primary-700">
        <div className="px-6 py-8 sm:px-8">
          <div className="flex flex-col space-y-6 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="flex flex-shrink-0 justify-center items-center w-16 h-16 bg-white bg-opacity-20 rounded-full">
                <Rocket className="w-8 h-8 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-2xl font-bold text-white">
                  {t("admin.cta.onboardFacility.title")}
                </h3>
                <p className="mt-2 text-base text-primary-100">
                  {t("admin.cta.onboardFacility.subtitle")}
                </p>
              </div>
            </div>
            <div className="flex flex-col flex-shrink-0 gap-3 sm:flex-row sm:gap-4">
              <Button
                variant="outline"
                onClick={() => navigate("/admin/onboarding")}
                className="w-full sm:w-auto !bg-white !text-primary-600 hover:!bg-primary-50 font-semibold px-8 py-4 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md text-base !border-primary-200"
              >
                {t("admin.cta.onboardFacility.button")}
              </Button>
              <Button
                variant="outline"
                // MED-1451: /admin/docs is not a route — it fell through to the
                // catch-all and re-rendered this page. The documentation lives on
                // the user-governance "Docs & tools" tab.
                onClick={() =>
                  navigate("/admin/user-governance?tab=docs-tools")
                }
                className="w-full sm:w-auto !bg-white/20 !text-white !border-white hover:!bg-white hover:!text-primary-600 font-semibold px-8 py-4 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md text-base flex items-center justify-center space-x-2"
              >
                <FileText className="w-4 h-4" />
                <span>{t("admin.actions.viewDocumentation")}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {loadError && (
        <div
          role="alert"
          className="page-section rounded border border-red-400 bg-red-100 p-3 text-red-700"
        >
          {t("admin.stats.loadError")}
        </div>
      )}

      {/* Admin Statistics Cards */}
      <div className="grid grid-cols-1 gap-6 page-section md:grid-cols-2 lg:grid-cols-4">
        <StatisticsCard
          // MED-1452: this tile is licence-scoped — it counts the facilities that hold a
          // licence, not the whole directory the Établissements page lists. The label
          // says so rather than leaving the two numbers looking like a contradiction.
          title={t("admin.stats.totalFacilities")}
          value={stats?.clientFacilitiesTotal ?? NO_VALUE}
          subtitle={
            stats
              ? `${stats.clientFacilitiesActive} ${t("admin.stats.activeUnderLicence")}`
              : undefined
          }
          icon={Building2}
          iconColor="text-primary-600"
          iconBgColor="bg-primary-100"
        />

        <StatisticsCard
          title={t("admin.stats.totalManagers")}
          value={stats?.managersTotal ?? NO_VALUE}
          subtitle={
            stats
              ? `${stats.managersAssigned} ${t("admin.stats.assigned")}`
              : undefined
          }
          icon={UserPlus}
          iconColor="text-success-600"
          iconBgColor="bg-success-100"
        />

        <StatisticsCard
          title={t("admin.stats.totalRevenue")}
          value={stats ? formatMoney(stats.revenueCurrentMonth) : NO_VALUE}
          subtitle={t("admin.stats.currentMonth")}
          trend={
            revenueTrend === null
              ? undefined
              : {
                  value: `${revenueTrend > 0 ? "+" : ""}${revenueTrend}% ${t("admin.stats.fromLastMonth")}`,
                  isPositive: revenueTrend >= 0,
                }
          }
          icon={DollarSign}
          iconColor="text-warning-600"
          iconBgColor="bg-warning-100"
        />

        <StatisticsCard
          title={t("admin.stats.pendingInvoices")}
          value={stats?.pendingInvoicesCount ?? NO_VALUE}
          subtitle={
            stats
              ? t("admin.stats.pendingAmount", {
                  amount: formatMoney(stats.pendingInvoicesAmount),
                })
              : undefined
          }
          icon={FileText}
          iconColor="text-secondary-600"
          iconBgColor="bg-secondary-100"
        />
      </div>

      {/* New sign-ups of the last 7 days (MED-1341) */}
      <NewSignUps />

      {/* Quick Actions */}
      <div className="grid grid-cols-1 gap-4 page-section md:grid-cols-2 lg:grid-cols-4">
        <Button
          variant="outline"
          className="flex flex-col justify-center items-center h-24 text-center hover:bg-primary-50 hover:border-primary-300"
          onClick={() => navigate("/admin/facilities")}
        >
          <Building2 className="mb-2 w-6 h-6 text-primary-600" />
          <span className="text-sm font-medium">
            {t("admin.actions.manageFacilities")}
          </span>
        </Button>
        <Button
          variant="outline"
          className="flex flex-col justify-center items-center h-24 text-center hover:bg-success-50 hover:border-success-300"
          onClick={() => navigate("/admin/managers")}
        >
          <UserPlus className="mb-2 w-6 h-6 text-success-600" />
          <span className="text-sm font-medium">
            {t("admin.actions.manageManagers")}
          </span>
        </Button>
        <Button
          variant="outline"
          className="flex flex-col justify-center items-center h-24 text-center hover:bg-secondary-50 hover:border-secondary-300"
          onClick={() => navigate("/admin/invoices")}
        >
          <CreditCard className="mb-2 w-6 h-6 text-secondary-600" />
          <span className="text-sm font-medium">
            {t("admin.actions.manageInvoices")}
          </span>
        </Button>
        <Button
          variant="outline"
          className="flex flex-col justify-center items-center h-24 text-center hover:bg-warning-50 hover:border-warning-300"
          onClick={() => navigate("/admin/features")}
        >
          <ToggleRight className="mb-2 w-6 h-6 text-warning-600" />
          <span className="text-sm font-medium">
            {t("admin.actions.manageFeatures")}
          </span>
        </Button>
      </div>

      {/* Platform insights (charts) */}
      {stats && <DashboardInsights stats={stats} />}
    </div>
  );
};
